import { Component, HostListener, Input, OnInit } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { GlobalService } from '../services/global.service';
import { AuthenticatorService } from '@aws-amplify/ui-angular';
import { ModalController, ToastController } from '@ionic/angular';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import MainScene from '../Scenes/MainScene';
import BaseLevelScene from '../Scenes/BaseLevelScene';
import { AuthSession, fetchAuthSession } from 'aws-amplify/auth';
import { environment } from 'environment';
import { Message } from 'infrastructure/lib/src/models/messages';
import { getCurrentUser } from 'aws-amplify/auth';
import { ProfileViewerComponent } from '../profile-viewer/profile-viewer.component';

interface MessagesResponse{
  messages: Message[];
  lastEvaluatedKey?: string;
}

@Component({
  selector: 'app-send-message-modal',
  templateUrl: './send-message-modal.component.html',
  styleUrls: ['./send-message-modal.component.scss'],
})
export class SendMessageModalComponent  implements OnInit {
  @Input() recipient: string;
  @Input() displayProfileButton: boolean = false;

  private webSocket: WebSocket;

  messageText$ = new BehaviorSubject<string>("");
  scene: MainScene | BaseLevelScene;
  session: AuthSession;
  currentUser: string;
  selectedMessageId: number | null = null;
  intervalId: NodeJS.Timeout;
  isLoading: boolean = false;
  lastEvaluatedKey: any = null;
  hasMoreMessages: boolean = true;
  sendSound = new Audio('assets/audio/spell2.wav');
  receiveSound = new Audio('assets/audio/spell1_0.wav');

constructor(
  private modalCtrl: ModalController,
  private http: HttpClient,
  private toastController: ToastController,
  public globalService: GlobalService,
  public authenticator: AuthenticatorService,
) { }

  async ngOnInit() {
    this.isLoading = false;
    this.session = await fetchAuthSession();
    // this.intervalId = setInterval(() => {
    //   this.retrieveMessages();
    // }, 3000);
    this.initializeWebSocket();
    await this.loadInitialMessages();
  }

  async loadInitialMessages(){
    try {
      // Reset pagination
      this.lastEvaluatedKey = null;
      this.hasMoreMessages = true;
      GlobalService.$conversation.next([]);

      await this.retrieveMessages(true, true);
    } catch (error) {
      console.error('Error loading initial comments:', error);
    }
  }

  async retrieveMessages(displayErrorToast=false, isInitialLoad=false): Promise<{hasMore: boolean}>{
    console.log("Retrieving messages...");

    if(this.isLoading || !this.hasMoreMessages){
      return { hasMore: false };
    }

    try {
      this.isLoading = true;
      const { username } = await getCurrentUser();
      this.currentUser = username;

      // Build query parameters
      let params = new HttpParams()
        .set('otherUser', this.recipient);

      if (!isInitialLoad && this.lastEvaluatedKey) {
        params = params.set('lastEvaluatedKey', 
          encodeURIComponent(JSON.stringify(this.lastEvaluatedKey)));
      }

      // Convert the subscription to a Promise.
      return new Promise((resolve, reject) => {
        // Retrieve the conversations with a particular user and the current user.
        this.http.get(`${environment.REST_API}/conversation`,{
          params: params,
          headers: new HttpHeaders({
            Authorization: `Bearer ${this.session.tokens.idToken}`
          }),
        }).subscribe({
          next: (data: MessagesResponse) => {
              console.log("messages data", data);
              const retrievedMessages = this.formatConversation(data.messages || []);
              if(isInitialLoad){
                GlobalService.$conversation.next(retrievedMessages);
              }
              else{
                // Append messages for infinite scroll.
                GlobalService.$conversation.next([
                  ...GlobalService.$conversation.getValue(),
                  ...retrievedMessages
                ]
                );
              }
              this.lastEvaluatedKey = data.lastEvaluatedKey;
              this.hasMoreMessages = !!data.lastEvaluatedKey;
              this.markAsRead();
              this.isLoading = false;
              console.log("lastEvaluatedKey:", data.lastEvaluatedKey);
              resolve({ hasMore: !!data.lastEvaluatedKey });
          },
          error: async error => {
            this.isLoading = false;
            reject(error);
            throw error;
          }
        });
      });
    } catch (error) {
      console.error('Error getting current user:', error);
      if(displayErrorToast){
        const toast = await this.toastController.create({
          message: `There was an error retrieving your conversation with ${this.recipient}.`,
          duration: 2000,
          color: 'danger',
          position: 'top',
        });
        this.isLoading = false;
        await toast.present();
      }
      return { hasMore: false };
    }
  }

  // Handle infinite scroll
  async loadMore(event: any, isInitialLoad=false) {
    if (this.isLoading) {
      return;
    }

    try {
      const result = await this.retrieveMessages(true, isInitialLoad);
      event.target.complete();

      // Disable infinite scroll if no more data
      if (!result.hasMore) {
        event.target.disabled = true;
      }
    } catch (error) {
      console.error('Error loading more comments:', error);
      event.target.complete();
    }
  }

  sendMessagePOST(){
    const message = this.messageText$.getValue();
    this.http.post(`${environment.REST_API}/messages`,
      {recipient: this.recipient, messageText: message},
      {
        headers: new HttpHeaders({
          Authorization: `Bearer ${this.session.tokens.idToken}`
        }),
        }
    ).subscribe({
      next: async message => {
        this.messageText$.next("");

        // Add new message to the beginning of existing conversation
        const currentMessages = GlobalService.$conversation.getValue();
        const newMessage = this.formatConversation([message as Message])[0]; // Assuming your API returns the new message
        
        GlobalService.$conversation.next([
          newMessage,
          ...currentMessages
        ]);
      },
      error: async error => {
          const toast = await this.toastController.create({
            message: `There was an error sending a message to ${this.recipient}.`,
            duration: 2000,
            color: 'danger',
            position: 'top',
          });
          await toast.present();
      }
    });
  }

  // Add WebSocket handling for new messages
  private initializeWebSocket() {
    const token = this.session.tokens.idToken;
    this.webSocket = new WebSocket(`${environment.MESSAGING_WS_ENDPOINT}?Auth=${token}&recipient=${this.recipient}`);
    
    // Handle newly received messages.
    this.webSocket.onmessage = (event) => {
      const message = JSON.parse(event.data);
      console.log('Received message:', message);
      if (message.type === 'Message' && (message.sender === this.recipient || message.sender === this.currentUser)) {
        // Add new message to the beginning of conversation
        const currentMessages = GlobalService.$conversation.getValue();
        GlobalService.$conversation.next([
          message,
          ...currentMessages
        ]);
        if(message.sender != this.currentUser){
          this.receiveSound.play(); // Play receive sound
        }
      }
    };

    this.webSocket.onclose = (event) => {
      // WebSocket Close Event Codes:
      // 1000 - Normal closure (intentional)
      // 1001 - Going Away (page unload/server shutdown)
      // 1006 - Abnormal closure (connection lost)
      
      if (event.code !== 1000 && event.code !== 1001) {
        // Only reconnect on abnormal closures
        console.log('Connection lost. Attempting to reconnect...');
        setTimeout(() => this.initializeWebSocket(), 3000);
      } else {
        console.log('WebSocket closed normally');
      }
    };
  }

  // Send message using WebSockets.
  sendMessage() {
    const messageText = this.messageText$.getValue();
    if (messageText.trim()) {
      this.webSocket.send(JSON.stringify({
        action: 'sendMessage',
        recipient: this.recipient,
        messageText: messageText
      }));
      this.messageText$.next('');
      this.sendSound.play(); // Play send sound
    }
  }

  // Close the modal
  cancel() {
    if(this.scene){
      const activeScenes = this.scene.scene.manager.getScenes(true) as Array<BaseLevelScene>;
      activeScenes.forEach((scene) => {
        scene.scene.resume();
      });
    }
    GlobalService.$conversation.next([]);

    clearInterval(this.intervalId);
    return this.modalCtrl.dismiss(null, 'cancel');
  }

  trackNewMessageText(messageText: string){
    this.messageText$.next(messageText);
  }

  formatConversation(messages: Message[]){
    messages.sort((a, b) => {
      return b.timestamp - a.timestamp;
    });
    return messages;
  }

  getConversation(){
    return GlobalService.$conversation;
  };

  sentByCurrentUser(message: Message){
    return message.sender === this.currentUser;
  }

  formatTimestamp(timestamp: number){
    const date = new Date(timestamp);
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
      hour12: true,
    };
    return date.toLocaleString('en-US', options);
  }

  markAsRead(){
    const conversations = GlobalService.$conversationsMetadata.getValue();
    const updatedConversations = conversations.map((conversation) => {
      // Check if current user is userA and recipient is userB.
      if(conversation.userA === this.currentUser &&  conversation.userB === this.recipient){
        conversation.lastViewedByA = Date.now();
      }
      // Check if current user is userB and recipient is userA.
      if(conversation.userB === this.currentUser &&  conversation.userA === this.recipient){
        conversation.lastViewedByB = Date.now();
      }
      return conversation;
    });
    GlobalService.$conversationsMetadata.next(conversations);
  }

  // Handle message deletion.
  deleteMessage(messageId: string){
    console.log("Deleting message: ", messageId);
    this.http.delete(`${environment.REST_API}messages?messageId=${messageId}`,{
      headers: new HttpHeaders({
        Authorization: `Bearer ${this.session.tokens.idToken}`
      }),
      }
    ).subscribe({
      next: async data => {

        // Remove the deleted message from the conversation
        const currentMessages = GlobalService.$conversation.getValue();
        GlobalService.$conversation.next(
          currentMessages.filter(msg => msg.messageId !== messageId)
        );

        const toast = await this.toastController.create({
          message: `Message deleted!`,
          duration: 2000,
          color: 'success',
          position: 'top',
        });
        await toast.present();
      },
      error: async error => {
          const toast = await this.toastController.create({
            message: `There was an error deleting this message.`,
            duration: 2000,
            color: 'danger',
            position: 'top',
          });
          await toast.present();
      }
    });
  }

  onMessageDoubleClick(messageId: number, event: MouseEvent) {
    event.stopPropagation();
    console.log("double clicked!!");
    console.log(messageId);
    // Small delay to prevent the document click handler from immediately closing
    setTimeout(() => {
      this.selectedMessageId = messageId;
    }, 0);
  }

  @HostListener('document:click', ['$event'])
  onClickOutside(event: MouseEvent) {
    // Check if click is outside the card
    const clickedElement = event.target as HTMLElement;
    if (!clickedElement.closest('ion-card')) {
      this.selectedMessageId = null;
    }
  }

  async openProfileModal(username: string){
    console.log(`viewing ${username}'s profile`);
    await this.modalCtrl.dismiss();
    this.modalCtrl.create({
      component: ProfileViewerComponent,
      componentProps: { 
        username: username,
        scene: null
      }
    }).then((modal) => {
      console.log("Presenting the profile modal");
      modal.present();
      GlobalService.incrementModalCount();
      modal.onDidDismiss().then(() => {
        GlobalService.decrementModalCount();
      });
    });
  }

  ngOnDestroy() {
    if (this.webSocket) {
      console.log("Closing Messaging WebSocket connection");
      // Use code 1000 for normal closure
      this.webSocket.close(1000, 'User initiated disconnect');
    }
  }

}
